import {
  OnsiteTriggers,
  ResponseSegmentationRules,
  SubjectLine,
  TemplateTypes,
  TriggersConfigMap,
} from 'sections/onsite-campaigns/types';
import { OnsiteMessageDisplayType } from 'sections/onsite-campaigns/types/enums/OnsiteMessageDisplayType';
import { convertSegmentRuleValues } from '../utils/convertSegmentRuleValues';
import { TemplateInfo } from '../types';
import { CampaignMessage } from '../../../contexts/CampaignEditorState';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';
import { TemplatePosition } from 'entities/campaign/rulesets/template-position';
import { mapTriggersToState } from '../utils/mapTriggersToState';
import moment from 'moment-timezone';

export interface CampaignResponseModel {
  showSegmentationSplit: boolean;
  onsiteMessageSegments?: {
    segmentId: string;
    segmentationRules: ResponseSegmentationRules;
    controlGroupEnabled?: boolean;
    controlGroupWeight?: number;
    branchName?: string;
    branchId?: string;
    startDate?: number;
    endDate?: number;
    variants: {
      id: string;
      name: string;
      isDefault: boolean;
      templateId: string;
      displayType: OnsiteMessageDisplayType;
      selector?: string;
      position?: TemplatePosition;
      triggers?: OnsiteTriggers;
      segmentationRules?: ResponseSegmentationRules;
    }[];
    offsiteMessageSegments: {
      segmentId: string;
      variants: {
        id: string;
        name: string;
        messageTemplateId: string;
        subjectLineId: string;
      }[];
    }[];
  }[];
}

type OnsiteMessageSegment = {
  id: string;
  name: string;
  segmentId: string;
  segmentationRules: ResponseSegmentationRules;
  controlGroupEnabled?: boolean;
  controlGroupWeight?: number;
  branchName?: string;
  branchId?: string;
  scheduling?: {
    startDate: Date;
    startTime: string;
    endDate: Date;
    endTime: string;
  };
  template: {
    id: string;
    name: string;
    type: string;
    supportedLanguage: string;
    isEmbedded: boolean;
  };
  triggers: TriggersConfigMap;
  messageType: 'onsite';
  displayType: OnsiteMessageDisplayType;
  isDefault: boolean;
  embeddedSelector: string;
  embeddedPosition: string;
};

export interface TemplateResponseModel {
  id: string;
  templateName: string;
  supportedLanguages: {
    language: string;
  }[];
  type: string;
  isEmbedded: boolean;
}

export interface SubjectLineResponseModel {
  id: string;
  subjectLines: Record<string, string>;
}

export class MessagesMapper {
  public static Map(
    response: CampaignResponseModel,
    templateResponse: TemplateResponseModel[],
    subjectLineResponse: SubjectLineResponseModel[],
    preferredLanguage: string,
  ): { [key: string]: CampaignMessage<any> } {
    const templateDictionary: { [id: string]: TemplateInfo } = templateResponse.reduce((prev, current) => {
      return {
        ...prev,
        [current.id]: {
          templateName: current.templateName,
          supportedLanguage: current.supportedLanguages?.[0]?.language ?? '',
          type: current.type as TemplateTypes,
          isEmbedded: current.isEmbedded,
        },
      };
    }, {});

    const subjectLineDictionary: { [id: string]: SubjectLine } = subjectLineResponse?.reduce((prev, current) => {
      return {
        ...prev,
        [current.id]: {
          id: current.id,
          label: getFirstNonEmptyValue(current.subjectLines, preferredLanguage), // show latest updated subject line value
        },
      };
    }, {});

    let messages = {};

    response.onsiteMessageSegments
      ?.filter(s => s.segmentId || s.offsiteMessageSegments?.length)
      ?.forEach(onsiteSegment => {
        /*
          We should only create the onsite messages if we have any variants. If there is
          any onsite message then we know that we're not in a send email only campaign and
          we need to map the rest of the offsite messages.
        */
        onsiteSegment?.variants?.forEach(onsiteMessage => {
          const template = templateDictionary[onsiteMessage.templateId];
          const scheduling = {} as any;
          if (onsiteSegment.startDate) {
            scheduling.startDate = new Date(onsiteSegment.startDate);
            scheduling.startTime = moment(onsiteSegment.startDate).format('HH:mm') ?? '00:00';
          }
          if (onsiteSegment.endDate) {
            scheduling.endDate = new Date(onsiteSegment.endDate);
            scheduling.endTime = moment(onsiteSegment.endDate).format('HH:mm') ?? '23:59';
          }

          const message: OnsiteMessageSegment = {
            id: onsiteMessage.id,
            name: onsiteMessage.name,
            segmentId: onsiteSegment.segmentId,
            isDefault: onsiteMessage.isDefault,
            branchName: onsiteSegment.branchName,
            branchId: onsiteSegment.branchId,
            template: template
              ? {
                  id: onsiteMessage.templateId,
                  name: template?.templateName,
                  type: template?.type,
                  supportedLanguage: template?.supportedLanguage,
                  isEmbedded: template?.isEmbedded,
                }
              : undefined,
            displayType: OnsiteMessageDisplayType.Overlay,
            segmentationRules: convertSegmentRuleValues(
              onsiteMessage.segmentationRules ?? onsiteSegment.segmentationRules,
            ),
            messageType: 'onsite',
            controlGroupEnabled: onsiteSegment.controlGroupEnabled,
            controlGroupWeight: onsiteSegment.controlGroupEnabled ? onsiteSegment.controlGroupWeight : undefined,
            embeddedSelector: onsiteMessage.selector,
            embeddedPosition: onsiteMessage.position,
            triggers: mapTriggersToState(onsiteMessage.triggers),
          };
          if (Object.keys(scheduling).length) {
            message.scheduling = scheduling;
          }

          messages = {
            ...messages,
            [onsiteMessage.id]: message,
          };
        });

        onsiteSegment.offsiteMessageSegments?.forEach(offsiteSegment => {
          offsiteSegment.variants?.forEach(offsiteMessage => {
            const template = templateDictionary[offsiteMessage.messageTemplateId];
            const subjectLine = subjectLineDictionary[offsiteMessage.subjectLineId];

            messages = {
              ...messages,
              [offsiteMessage.id]: {
                id: offsiteMessage.id,
                name: offsiteMessage.name,
                segmentId: offsiteSegment.segmentId,
                onsiteSegmentId: onsiteSegment.segmentId,
                subjectLine,
                template: template
                  ? {
                      id: offsiteMessage.messageTemplateId,
                      name: template.templateName,
                      supportedLanguage: template.supportedLanguage,
                      type: template.type,
                      isEmbedded: template.isEmbedded,
                    }
                  : undefined,
                messageType: 'offsite',
              },
            };
          });
        });

        /*
          If variants are empty then we need to check if this is a send email only campaign. This check ensures that 
          we don't re-add any offsite messages that were added when looping through variants.
        */
        const isSendEmailOnly =
          !response.showSegmentationSplit && !onsiteSegment.segmentId && onsiteSegment.offsiteMessageSegments?.length;

        if (isSendEmailOnly) {
          onsiteSegment.offsiteMessageSegments?.forEach(offsiteSegment => {
            offsiteSegment.variants?.forEach(offsiteMessage => {
              const template = templateDictionary[offsiteMessage.messageTemplateId];
              const subjectLine = subjectLineDictionary[offsiteMessage.subjectLineId];

              messages = {
                ...messages,
                [offsiteMessage.id]: {
                  id: offsiteMessage.id,
                  segmentId: offsiteSegment.segmentId,
                  name: offsiteMessage.name,
                  onsiteSegmentId: onsiteSegment.segmentId,
                  subjectLine,
                  template: template
                    ? {
                        id: offsiteMessage.messageTemplateId,
                        name: template.templateName,
                        supportedLanguage: template.supportedLanguage,
                        type: template.type,
                        isEmbedded: template.isEmbedded,
                      }
                    : undefined,
                  messageType: 'offsite',
                },
              };
            });
          });
        }
      });

    return messages;
  }
}
